import {z} from "zod/v4-mini"
import type {NodeParameters} from "@cm/graph-v3"
import type {EvaluableTemplateNode} from "#template/evaluable-template-node"
import {hasMarker} from "#template/role-markers"
import type {TemplateNodeDefinition} from "#template/template-node/types"
import type {GeometryAttributeData, IndexingDim} from "#template/nodes/geometry-graph/interfaces"
import {GeometryGraphNode} from "#template/nodes/geometry-graph/common/geometry-graph-node"

export abstract class GeometryAttributeNode<out T extends IndexingDim, out Parameters extends NodeParameters = NodeParameters> extends GeometryGraphNode<
    GeometryAttributeData<T>,
    Parameters
> {
    public abstract override get definition(): TemplateNodeDefinition<Parameters>

    protected override get evaluatedGeometryDataPrefix() {
        return "geometryAttributeData"
    }
}

////////////////////////////////////////////////////////////////////////////////
// Point dim (T = 0)

export const pointGeometryAttributeLikeMarker = Symbol("PointGeometryAttributeLike")

export interface PointGeometryAttributeLikeNode extends EvaluableTemplateNode<GeometryAttributeData<0> | null> {
    readonly [pointGeometryAttributeLikeMarker]: true
}

export const isPointGeometryAttributeLikeNode = (x: unknown): x is PointGeometryAttributeLikeNode => hasMarker(x, pointGeometryAttributeLikeMarker)

export const pointGeometryAttributeLike = z.custom<PointGeometryAttributeLikeNode>(isPointGeometryAttributeLikeNode)

////////////////////////////////////////////////////////////////////////////////
// Curve dim (T = 1)

export const curveGeometryAttributeLikeMarker = Symbol("CurveGeometryAttributeLike")

export interface CurveGeometryAttributeLikeNode extends EvaluableTemplateNode<GeometryAttributeData<1> | null> {
    readonly [curveGeometryAttributeLikeMarker]: true
}

export const isCurveGeometryAttributeLikeNode = (x: unknown): x is CurveGeometryAttributeLikeNode => hasMarker(x, curveGeometryAttributeLikeMarker)

export const curveGeometryAttributeLike = z.custom<CurveGeometryAttributeLikeNode>(isCurveGeometryAttributeLikeNode)

////////////////////////////////////////////////////////////////////////////////
// Mesh dim (T = 2)

export const meshGeometryAttributeLikeMarker = Symbol("MeshGeometryAttributeLike")

export interface MeshGeometryAttributeLikeNode extends EvaluableTemplateNode<GeometryAttributeData<2> | null> {
    readonly [meshGeometryAttributeLikeMarker]: true
}

export const isMeshGeometryAttributeLikeNode = (x: unknown): x is MeshGeometryAttributeLikeNode => hasMarker(x, meshGeometryAttributeLikeMarker)

export const meshGeometryAttributeLike = z.custom<MeshGeometryAttributeLikeNode>(isMeshGeometryAttributeLikeNode)
